import React, { useState } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import "@/App.css";
import { AuthProvider, useAuth } from "@/lib/auth";
import { useSettings } from "@/lib/useSettings";
import { Toaster } from "@/components/ui/sonner";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import LearningHub from "@/pages/LearningHub";
import LessonDetail from "@/pages/LessonDetail";
import Pathway from "@/pages/Pathway";
import Protocol from "@/pages/Protocol";
import Community from "@/pages/Community";
import Events from "@/pages/Events";
import Pricing from "@/pages/Pricing";
import AdminPanel from "@/pages/AdminPanel";
import AuthCallback from "@/pages/AuthCallback";
import MaintenancePage from "@/pages/MaintenancePage";
import PaymentSuccess, { PaymentCancel } from "@/pages/PaymentSuccess";
import BookPage, { BookingSuccess, BookingCancel } from "@/pages/BookPage";
import Contact from "@/pages/Contact";
import BookSession from "@/pages/BookSession";
import { MagicLinkSetup, BookingSuccessPublic, BookingCancelPublic } from "@/pages/BookSessionFlow";
import NotificationSettings from "@/pages/NotificationSettings";
import MyIntakePage from "@/pages/MyIntakePage";
import UnsubscribePage from "@/pages/UnsubscribePage";

function ProtectedRoute({ children, adminOnly }) {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center text-white/50 text-sm" data-testid="auth-loading">
        Loading…
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && !user.is_admin) return <Navigate to="/dashboard" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  const { user } = useAuth();
  const { settings } = useSettings({ poll: true });
  const [adminBypass, setAdminBypass] = useState(false);

  // Synchronously detect Emergent OAuth callback fragment
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  // Maintenance gate: block non-admins from the app.
  // The /login route stays open so admins can sign in and toggle it off.
  if (settings.maintenance_mode && !user?.is_admin && !adminBypass
      && location.pathname !== "/login") {
    return (
      <MaintenancePage
        settings={settings}
        isAdmin={!!user?.is_admin}
        onOpenSite={() => setAdminBypass(true)}
      />
    );
  }

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/contact" element={<Contact />}/>
      <Route path="/book-session" element={<BookSession />} />
      <Route path="/book-session/success" element={<BookingSuccessPublic />} />
      <Route path="/book-session/cancel" element={<BookingCancelPublic />} />
      <Route path="/auth/setup" element={<MagicLinkSetup />} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/learn" element={<ProtectedRoute><LearningHub /></ProtectedRoute>} />
      <Route path="/learn/:id" element={<ProtectedRoute><LessonDetail /></ProtectedRoute>} />
      <Route path="/pathway" element={<ProtectedRoute><Pathway /></ProtectedRoute>} />
      <Route path="/protocol" element={<ProtectedRoute><Protocol /></ProtectedRoute>} />
      <Route path="/community" element={<ProtectedRoute><Community /></ProtectedRoute>} />
      <Route path="/events" element={<ProtectedRoute><Events /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute adminOnly><AdminPanel /></ProtectedRoute>} />
      <Route path="/payment/success" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />
      <Route path="/payment/cancel" element={<ProtectedRoute><PaymentCancel /></ProtectedRoute>} />
      <Route path="/book" element={<ProtectedRoute><BookPage /></ProtectedRoute>} />
      <Route path="/booking/success" element={<ProtectedRoute><BookingSuccess /></ProtectedRoute>} />
      <Route path="/booking/cancel" element={<ProtectedRoute><BookingCancel /></ProtectedRoute>} />
      <Route path="/settings/notifications" element={<ProtectedRoute><NotificationSettings /></ProtectedRoute>} />
      <Route path="/my-intake" element={<ProtectedRoute><MyIntakePage /></ProtectedRoute>} />
      <Route path="/unsubscribe" element={<UnsubscribePage />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
          <Toaster theme="dark" position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}
